import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { InfoIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { fetchChainState } from '@/api'
import { uploadIpfsJson } from '@/api/modules/ipfs'
import GradientWrapper from '@/common/GradientWrapper'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import TiltedCard from '@/common/TiltedCard'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  calculateCidFromBytes,
  emitter,
  formatAddress,
  stableJsonStringify,
} from '@/helpers'
import { ErrorHandler } from '@/helpers/error-handler'
import { buildMintNftMsg, MintError } from '@/libs/keplr/messages'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

const OWNER_PREFIX = `${bridgelessChainInfo.bech32Config.bech32PrefixAccAddr}1`

const mintSchema = z.object({
  owner: z
    .string()
    .trim()
    .min(1, 'Owner address is required')
    .refine(
      v => v.startsWith(OWNER_PREFIX),
      `Owner address must start with "${OWNER_PREFIX}"`,
    ),
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Name must be at most 100 characters'),
  description: z
    .string()
    .trim()
    .min(1, 'Description is required')
    .max(1000, 'Description must be at most 1000 characters'),
})

type MintFormValues = z.infer<typeof mintSchema>

const PLACEHOLDER_IMAGE_SRC = `${import.meta.env.BASE_URL}images/nft-preview.png`

export default function MintModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { signAndBroadcast, address } = useKeplrWallet()

  const form = useForm<MintFormValues>({
    resolver: zodResolver(mintSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      owner: '',
      name: '',
      description: '',
    },
  })

  const { data: chainState } = useQuery({
    queryKey: queryKeys.rpc.chainState(),
    queryFn: fetchChainState,
    enabled: open,
    staleTime: 30_000,
  })

  const startBlock = useMemo(() => {
    const height = Number(chainState?.last_block_height ?? 0)
    return height ? BigInt(height + 100) : null
  }, [chainState])

  const currentHeight = useMemo(() => {
    return Number(chainState?.last_block_height ?? 0)
  }, [chainState])

  const previewName = useWatch({
    control: form.control,
    name: 'name',
  })

  const name = previewName || 'Shard'

  const previewImageSrc = PLACEHOLDER_IMAGE_SRC

  const mintTx = useMutation({
    mutationKey: queryKeys.tx.mintNft(),
    mutationFn: async (values: MintFormValues) => {
      if (!address) {
        throw new Error('Wallet not connected')
      }
      if (!startBlock) {
        throw new Error('Start block not available')
      }

      const metadata = {
        name: values.name.trim(),
        description: values.description.trim(),
      }

      const metadataJson = stableJsonStringify(metadata)
      const metadataCid = await calculateCidFromBytes(
        new TextEncoder().encode(metadataJson),
      )

      const msg = buildMintNftMsg({
        creator: address,
        owner: values.owner,
        startVestingBlock: startBlock,
        metadataCid,
      })

      const txHash = await signAndBroadcast([msg])

      return { txHash, metadataCid, metadataJson }
    },
    onSuccess: result => {
      emitter.emit('success', {
        title: 'NFT minted',
        message: `TxHash: ${formatAddress(result.txHash)}`,
      })

      // Upload only metadata JSON to IPFS and verify CID.
      void (async () => {
        try {
          const parsedMetadata = JSON.parse(result.metadataJson)
          const uploadedMetadataCid = await uploadIpfsJson(parsedMetadata)

          if (uploadedMetadataCid !== result.metadataCid) {
            emitter.emit('warning', {
              title: 'Minted, but IPFS upload mismatch',
              message: `Metadata CID mismatch. Expected ${result.metadataCid}, got ${uploadedMetadataCid}`,
            })
          }
        } catch (error) {
          ErrorHandler.process(
            error,
            'Minted, but failed to upload metadata to IPFS',
          )
        }
      })()

      onClose()
      form.reset()
    },
    onError: error => {
      ErrorHandler.process(MintError.fromUnknown(error))
    },
  })

  const isLoading = mintTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isLoading && onClose()}
      title={<span className='text-foreground'>Mint NFT</span>}
      content={
        <form
          onSubmit={form.handleSubmit(values => mintTx.mutate(values))}
          className='text-foreground mt-4 flex flex-col gap-4 p-1'
        >
          <div className='bg-primary/10 mt-4 mb-2 flex items-center gap-2 rounded-md p-3 text-xs text-white'>
            <div className='flex flex-col gap-0.5'>
              <span className='flex items-center gap-2'>
                <InfoIcon className='size-3.5 shrink-0' />
                <span className='text-[13px]'>
                  Start vesting block{' '}
                  <strong>
                    {startBlock ? startBlock.toString() : 'Loading...'}
                  </strong>
                </span>
              </span>
              <span className='ml-5.5 text-[13px]'>
                Vesting begins when the chain reaches this height. Current
                block: <strong>{currentHeight || '...'}</strong>
              </span>
            </div>
          </div>

          {/* Owner */}
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Owner address</span>
            <GradientWrapper>
              <input
                {...form.register('owner')}
                disabled={isLoading}
                placeholder={`${OWNER_PREFIX}1...`}
                className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
              />
            </GradientWrapper>
            {form.formState.errors.owner && (
              <span className='text-xs text-red-400'>
                {form.formState.errors.owner.message}
              </span>
            )}
            {!form.formState.errors.owner && (
              <span className='muted-foreground mt-2 text-xs'>
                Must start with "{OWNER_PREFIX}" (bech32 address).
              </span>
            )}
          </div>

          <div className='flex flex-col gap-1'>
            <div className='mx-auto mt-5 flex w-full items-center justify-center sm:w-56'>
              <div className='aspect-square w-full'>
                <div className='text-muted-foreground/80 mb-4 w-full truncate text-center text-sm'>
                  {name} Preview
                </div>

                <TiltedCard
                  imageSrc={previewImageSrc}
                  altText='Shard preview'
                  containerHeight='100%'
                  containerWidth='100%'
                  imageHeight='100%'
                  imageWidth='100%'
                  scaleOnHover={1.03}
                  rotateAmplitude={10}
                  showMobileWarning={false}
                  showTooltip={false}
                />

                <div className='mb-4' />
              </div>
            </div>
            <span className='text-muted-foreground text-xs'>Name</span>
            <GradientWrapper>
              <input
                {...form.register('name')}
                disabled={isLoading}
                maxLength={100}
                placeholder='Bridgeless Vesting NFT'
                className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
              />
            </GradientWrapper>
            {form.formState.errors.name && (
              <span className='text-xs text-red-400'>
                {form.formState.errors.name.message}
              </span>
            )}
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Description</span>
            <GradientWrapper className='h-25'>
              <textarea
                {...form.register('description')}
                disabled={isLoading}
                rows={3}
                placeholder='NFT representing vesting-based BRIDGE rewards in the Bridgeless network.'
                className='bg-input text-foreground border-border h-25 w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
              />
            </GradientWrapper>
            {form.formState.errors.description && (
              <span className='text-xs text-red-400'>
                {form.formState.errors.description.message}
              </span>
            )}
          </div>
        </form>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isLoading}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={!startBlock || isLoading}
            onClick={form.handleSubmit(values => mintTx.mutate(values))}
          >
            {isLoading ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Minting…
              </span>
            ) : (
              'Mint'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-2xl' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
